import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import type { FormControl } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { fadeInOutAnimation } from '@freelancer/animations';
import type {
  BackendAllErrorCodes,
  DatastoreCollection,
} from '@freelancer/datastore';
import { Datastore } from '@freelancer/datastore';
import type {
  RecommendedUsername,
  RecommendedUsernamesCollection,
} from '@freelancer/datastore/collections';
import { ResponseData } from '@freelancer/freelancer-http';
import type { LoginError } from '@freelancer/login-signup';
import { LoginSignupService } from '@freelancer/login-signup';
import { Tracking } from '@freelancer/tracking';
import { trackById } from '@freelancer/ui';
import {
  AutoFocusOption,
  InputComponent,
  InputSize,
  InputType,
} from '@freelancer/ui/input';
import { BackgroundColor, LogoSize } from '@freelancer/ui/logo';
import { SpinnerSize } from '@freelancer/ui/spinner';
import { dirtyAndValidate } from '@freelancer/ui/validators';
import { UntilDestroy } from '@ngneat/until-destroy';
import type { Subscription, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs';

@UntilDestroy({ className: 'UsernameSelectFormComponent' })
@Component({
  standalone: false,
  selector: 'app-username-select-form',
  template: `
    <app-form-logo
      [showBackButton]="true"
      [size]="logoSize"
      [backTrackingLabel]="'UsernameSelectForm-Back'"
      [backgroundColor]="BackgroundColor.THEME"
      (back)="back.emit()"
    ></app-form-logo>
    @if (isRecommendedUserNameComplete$ | flAsync) {
      <form
        class="Form"
        [@fadeInOutAnimation]="{
          value: undefined,
          params: {
            duration: 100,
            delay: 0,
          },
        }"
        [formGroup]="formGroup"
        (ngSubmit)="onSubmit()"
      >
        <h1
          class="text-mid mb-mid"
          i18n="Signup username heading"
        >
          Choose a username
        </h1>
        <p
          class="text-left mb-mid"
          i18n="Signup username description"
        >
          Please note that a username cannot be changed once chosen.
        </p>
        <fl-input
          class="mb-xsmall"
          flTrackingLabel="UsernameSelectForm-UsernameSelectInput"
          placeholder="Username"
          i18n-placeholder="Signup username input placeholder"
          [control]="formGroup.controls.username"
          [disabled]="response === null"
          [size]="InputSize.LARGE"
          [type]="InputType.TEXT"
          [autofocus]="AutoFocusOption.ALWAYS"
          #focusInput
        ></fl-input>
        @if (recommendedUsernames$ | flAsync; as suggestions) {
          <div class="Suggestions mb-large">
            <p
              i18n="Username suggestions"
              class="text-foreground text-xsmall"
            >
              Suggestions:
              @for (
                suggestion of suggestions;
                track trackById(i, suggestion);
                let i = $index
              ) {
                @if (i > 0) {
                  <span> / </span>
                }
                <fl-link
                  class="mr-xxxsmall"
                  flTrackingLabel="UsernameSelectForm-UsernameSuggestionLink"
                  (click)="
                    formGroup.patchValue({ username: suggestion.username })
                  "
                >
                  {{ suggestion.username }}
                </fl-link>
              }
            </p>
          </div>
        }
        @if (response && response.status === 'error') {
          <app-login-signup-errors
            class="mb-small"
            [response]="response"
            (switchToLogin)="switchToLogin.emit()"
          ></app-login-signup-errors>
        }
        <app-login-signup-button
          i18n="Signup username next button"
          [busy]="busy"
          [stepLabel]="'UsernameSelectForm'"
        >
          Next
        </app-login-signup-button>
      </form>
    } @else {
      <div class="SpinnerContainer">
        <fl-spinner
          flTrackingLabel="UsernameSelectForm-LoadingSpinner"
          [size]="SpinnerSize.LARGE"
        ></fl-spinner>
      </div>
    }
  `,
  styleUrls: ['./username-select-form.component.scss'],
  animations: [fadeInOutAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsernameSelectFormComponent implements OnInit, OnDestroy {
  AutoFocusOption = AutoFocusOption;
  BackgroundColor = BackgroundColor;
  InputSize = InputSize;
  InputType = InputType;
  SpinnerSize = SpinnerSize;
  trackById = trackById;

  recommendedUsernamesCollection: DatastoreCollection<RecommendedUsernamesCollection>;
  recommendedUsernames$: Observable<readonly RecommendedUsername[]>;
  isRecommendedUserNameComplete$?: Observable<boolean>;

  recommendedUsernameSubscription: Subscription;
  recommnededUsernameCompleteSubscription: Subscription;

  @Input() baseName: string;
  @Input() email: string;
  @Input() formGroup: FormGroup<{ username: FormControl<string> }>;
  @Input() lastName: string;
  @Input() logoSize = LogoSize.MID;
  @Input() response?: ResponseData<unknown, BackendAllErrorCodes | LoginError>;
  @Input() busy = false;
  @Output() back = new EventEmitter();
  @Output() complete = new EventEmitter();
  @Output() switchToLogin = new EventEmitter();

  @ViewChild('focusInput') focusInput: InputComponent<string>;

  constructor(
    private datastore: Datastore,
    private loginSignupService: LoginSignupService,
    private tracking: Tracking,
  ) {}

  ngOnInit(): void {
    if (this.email) {
      this.recommendedUsernamesCollection =
        this.datastore.collection<RecommendedUsernamesCollection>(
          'recommendedUsernames',
          query =>
            this.baseName
              ? query
                  .where('email', '==', this.email)
                  .where(
                    'baseName',
                    '==',
                    this.baseName + (this.lastName ? this.lastName[0] : ''),
                  )
              : query.where('email', '==', this.email),
        );

      this.recommendedUsernames$ = this.recommendedUsernamesCollection
        .valueChanges()
        .pipe(shareReplay({ bufferSize: 1, refCount: true }));

      this.recommendedUsernameSubscription =
        this.recommendedUsernames$.subscribe(suggestions => {
          if (suggestions.length > 0) {
            this.formGroup.patchValue({ username: suggestions[0].username });
          }
        });

      this.isRecommendedUserNameComplete$ =
        this.recommendedUsernamesCollection.status$.pipe(
          map(status => status.ready || 'error' in status),
          distinctUntilChanged(),
        );

      this.recommnededUsernameCompleteSubscription =
        this.isRecommendedUserNameComplete$.subscribe(isComplete => {
          if (isComplete) {
            this.tracking.trackCustomEvent(
              'UserNameSelectFormRecommendedUsernamesComplete',
            );
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.recommendedUsernameSubscription?.unsubscribe();
    this.recommnededUsernameCompleteSubscription?.unsubscribe();
  }

  onSubmit(): void {
    if (this.response === null) {
      return;
    }

    // don't `dirtyAndValidate` if it's already invalid
    // so it won't clear the manual USERNAME_ALREADY_IN_USE error.
    if (this.formGroup.valid) {
      dirtyAndValidate(this.formGroup);
    }
    if (this.formGroup.valid) {
      const { username } = this.formGroup.controls;
      // Check whether the username is already registered with another account.
      this.loginSignupService
        .checkUserDetails({
          username: username.value,
        })
        .then(response => {
          if (response?.status === 'error') {
            username.setErrors({
              USERNAME_ALREADY_IN_USE:
                'This username is already registered. Please choose another one.',
            });
          } else {
            // Proceed to the next step if the username has not been registered.
            this.complete.emit();
          }
        });
    }
  }
}
